import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import { NO_DATA_AVAILABLE } from '../../../constants/common'
import {Flex, Form, Modal, Row, Space} from 'antd'
import {DownloadOutlined, ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, downloadFileBlob, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {PlantContext, SELECT_PLANT, SET_PLANT_LIST} from './context/context'
import PlantTable from './PlantTable'
import PlantFormModal from './form'
import PlantVehiclesForm from './form/PlantVehiclesForm'
import { notifyError } from '../../../utils/notifications'

const PlantList = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(PlantContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const [vehiclesPlant, setVehiclesPlant] = useState(null)
  const currentUrl = useRef('/api/plants/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const getPlantListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'plants.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_PLANT_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'plants.loadError' }))
    }
  }

  useEffect(() => {
    getPlantListData()
  }, [])

  useEffect(() => {
    const url = `/api/plants/?search=${search}`
    currentUrl.current = url
    getPlantListData()
  }, [debouncedSearch])

  const deletePlant = async (record) => {
    let res = await deleteRequest(`/api/plants/delete/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'plants.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getPlantListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'plants.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'plants.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'plants.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deletePlant(record),
      centered: true,
      maskClosable: true,
    })
  }

  const downloadPlantMakeModels = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'plants.downloadCaption' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(
      `/api/plants/download_plant_make_models/`,
      true,
      true
    )
    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    // `res?.msg` was redundant here: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type:
            res?.code === NO_DATA_AVAILABLE ? 'info' : 'error',
          msg: intl.formatMessage({
            id:
              res?.code === NO_DATA_AVAILABLE
                ? 'generic.noDataToExport'
                : 'plants.downloadError',
          }),
        },
      })
    } else {
      downloadFileBlob(res, 'plant_make_model.csv')
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'plants.downloadCompleted' }),
        },
      })
    }
  }

  const onCreatePlant = () => {
    dispatch({
      type: SELECT_PLANT,
      data: { selected_plant: {}, show_plant_popup: true },
    })
  }

  const onEditPlant = (record) => {
    dispatch({
      type: SELECT_PLANT,
      data: { selected_plant: record, show_plant_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.plant_list?.length) return []
    return state.plant_list.map((item, index) => ({
      ...item,
      key: `plant-${index}`,
      rowKey: `plant-${item.id}`,
      index,
    }))
  }, [state.plant_list])

  return (
    <>
      {contextHolder}

      {state.show_plant_popup && (
        <PlantFormModal getPlantListData={getPlantListData} />
      )}

      {vehiclesPlant && (
        <PlantVehiclesForm
          showModal={true}
          setShowModal={(value) => {
            if (!value) setVehiclesPlant(null)
          }}
          selectedPlant={vehiclesPlant}
        />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_plants"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'plants.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="search-plants"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Button
                  onClick={downloadPlantMakeModels}
                  icon={<DownloadOutlined />}
                >
                  {intl.formatMessage({ id: 'plants.export' })}
                </Button>
                <Button onClick={onCreatePlant}>
                  {intl.formatMessage({ id: 'plants.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <PlantTable
              dataSource={dataSource}
              pagination={pagination}
              getPlantListData={getPlantListData}
              onEdit={onEditPlant}
              onDelete={confirmDelete}
              onManageVehicles={(record) => setVehiclesPlant(record)}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default PlantList
